import {
  NodeDetailsType,
  Node,
  Connector,
  Graph,
  Port,
  WorkflowValidationError,
  WorkflowVersionDetail,
  WorkflowVersionList,
} from '../../app/modules/types/workflow'
import { apiClient } from '../client'
import { resolveComponentIcon } from '../componentIcons'
import { OWNS_COMPONENT_REFUSAL, OWNS_ERRORS } from '../ownsErrors'

export interface InactiveComponent {
  node_id: string
  component_id: string
  component_name: string
}

export interface RunPreflightResult {
  runnable: boolean
  inactive_components: InactiveComponent[]
  /**
   * Component NAMES in the workflow the user may not use (plan or role). Any
   * entry makes the run impossible — there is no "run anyway" for these.
   */
  not_permitted_components?: string[]
  /**
   * Why the SAVED graph no longer fits today's components (`runnable` is then
   * false). Absent on a backend that predates the field — read it as `[]`.
   */
  validation_errors?: WorkflowValidationError[]
}

// ─────────────────────────────────────────────────────────────────────────────
// Adapters: orch DTO shapes ↔ FE-internal shapes
// ─────────────────────────────────────────────────────────────────────────────
//
// The FE was originally written against Django's `apps.workflow` +
// `apps.schema_manager`, which used `node_type_id`, `position_x/_y`,
// `ports.target/source`, `last_update`, …
//
// The orch (NestJS) uses `component_id`, `position: {x,y}`, `ports.input/output`,
// `updated_at`, … Translation lives here so the rest of the FE keeps the
// names it already knows.

type OrchPort = { id: string; type?: string; label?: string; color?: string }

const orchPortToFePort = (p: OrchPort): Port => ({
  id: p.id,
  label: p.label ?? p.type ?? p.id,
  color: p.color ?? '',
})

const orchComponentToNodeDetails = (c: any): NodeDetailsType & { is_available?: boolean; config_schema?: any } => ({
  id: c.id,
  config: c.config,
  group: c.name ?? '',
  group_description: c.short_description ?? '',
  // Bundled icon resolved by component name; `c.icon` (a bare slug from the
  // orchestrator) is not a renderable URL, so it's only a secondary key.
  icon: resolveComponentIcon(c.name, c.icon),
  abstract_config: null,
  data: {
    label: c.display_name ?? c.name ?? '',
    details: c.short_description ?? null,
    long_description: c.long_description ?? null,
    ports: {
      target: (c.ports?.input ?? []).map(orchPortToFePort),
      source: (c.ports?.output ?? []).map(orchPortToFePort),
    },
  },
  // Legacy FE expects `is_available` to gate node draggability; orch reports
  // `is_active` (recent heartbeat). Map directly.
  is_available: c.is_active ?? false,
  // Surface FE-shape config schema so the drop handler (which spreads the
  // catalog item into a new node) carries it onto the canvas — fixes
  // "config button only appears after save+reload".
  config_schema: c.config_schema ?? null,
})

const orchGraphNodeToFeNode = (n: any): Node => ({
  id: n.id,
  node_type_id: n.component_id,
  // Orch sends the component name in `n.icon`; resolve it to the bundled asset
  // URL so a loaded workflow's canvas nodes show icons (same as the palette).
  icon: resolveComponentIcon(n.icon),
  config: n.config ?? {},
  abstract_config: n.abstract_config ?? null,
  config_schema: n.config_schema ?? null,
  position: { x: n.position?.x ?? 0, y: n.position?.y ?? 0 },
  data: n.data ?? {
    label: '',
    details: '',
    long_description: null,
    ports: { target: [], source: [] },
  },
})

const orchGraphConnectorToFeConnector = (c: any): Connector => ({
  id: c.id,
  source: c.source ?? c.source_id,
  target: c.target ?? c.target_id,
  sourceHandle: c.source_handle ?? c.source_handle_id ?? '',
  targetHandle: c.target_handle ?? c.target_handle_id ?? '',
})

const orchGraphToFeGraph = (g: any): Graph => ({
  nodes: (g?.nodes ?? []).map(orchGraphNodeToFeNode),
  connectors: (g?.connectors ?? []).map(orchGraphConnectorToFeConnector),
})

// Pass through extra fields the FE consumers read directly (`name_fa`,
// `description_fa`, `is_public`, etc) — graph is the only nested shape we
// have to remap. Return type stays `any` so we don't have to enumerate every
// passthrough field in the type system.
const orchWorkflowDetailToFe = (w: any): any => ({
  ...w,
  graph: orchGraphToFeGraph(w?.graph),
})

const feNodeToOrchSaveNode = (n: any) => ({
  id: n.id,
  component_id: n.node_type_id,
  position: n.position
    ? { x: n.position.x, y: n.position.y }
    : { x: n.position_x ?? 0, y: n.position_y ?? 0 },
  config: n.config ?? {},
})

const feConnectorToOrchSaveConnector = (c: any) => ({
  id: c.id,
  source: c.source,
  target: c.target,
  source_handle: c.sourceHandle ?? c.source_handle ?? '',
  target_handle: c.targetHandle ?? c.target_handle ?? '',
})

const feGraphPayloadToOrch = (payload: { nodes: any[]; connectors: any[] }) => ({
  nodes: (payload.nodes ?? []).map(feNodeToOrchSaveNode),
  connectors: (payload.connectors ?? []).map(feConnectorToOrchSaveConnector),
})

// ─────────────────────────────────────────────────────────────────────────────
// API surface
// ─────────────────────────────────────────────────────────────────────────────

export const workflowApi = {
  getList: async (params?: string): Promise<any> => {
    const url = params ? `/workflow/?${params}` : '/workflow/'
    const { data } = await apiClient.get(url)
    return data
  },

  getFilterOptions: async (): Promise<any> => {
    const { data } = await apiClient.get('/workflow/filter/')
    return data
  },

  getById: async (workflowId: string): Promise<any> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/`)
    return orchWorkflowDetailToFe(data)
  },

  /**
   * Advisory pre-run check: reports components in the workflow that are
   * currently inactive (no recent heartbeat). Always 200; the backend never
   * blocks — the FE owns the warning + bypass.
   */
  runPreflight: async (workflowId: string): Promise<RunPreflightResult> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/run-preflight/`, OWNS_COMPONENT_REFUSAL)
    return data
  },

  /**
   * Immutable, numbered versions of the graph, newest first. Created when a
   * workflow RUNS, not when it is saved. No trailing slash — these paths are
   * served without one. The viewer renders its own failures.
   */
  getVersions: async (workflowId: string, page: number, limit = 20): Promise<WorkflowVersionList> => {
    const { data } = await apiClient.get<WorkflowVersionList>(`/workflow/${workflowId}/versions`, {
      params: { page, limit },
      ...OWNS_ERRORS,
    })
    return data
  },

  /** One version, read-only. `number` is the integer, not the id. */
  getVersion: async (workflowId: string, number: number): Promise<WorkflowVersionDetail> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/versions/${number}`, OWNS_ERRORS)
    return { ...data, graph: orchGraphToFeGraph(data?.graph) }
  },

  /**
   * Load a version into the UNSAVED draft; the saved workflow only changes when
   * the user presses Save. 409 `DRAFT_EXISTS` unless `replace` — the caller
   * reads that refusal (`lib/workflowVersions.ts`), so it is never toasted.
   */
  restoreVersion: async (workflowId: string, number: number, replace = false): Promise<Graph> => {
    const { data } = await apiClient.post(`/workflow/${workflowId}/versions/${number}/restore`, undefined, {
      ...(replace ? { params: { replace: true } } : {}),
      ...OWNS_ERRORS,
    })
    return orchGraphToFeGraph(data)
  },

  create: async (payload: any): Promise<any> => {
    const { data } = await apiClient.post('/workflow/', payload)
    return data
  },

  update: async (workflowId: string, payload: any): Promise<any> => {
    const { data } = await apiClient.patch(`/workflow/${workflowId}/`, payload)
    return data
  },

  delete: async (workflowId: string): Promise<void> => {
    await apiClient.delete(`/workflow/${workflowId}/`)
  },

  /** Save workflow as draft (orch: PUT /workflow/<id>/draft/, Redis-backed). */
  saveDraft: async (workflowId: string, payload: { nodes: any[]; connectors: any[] }): Promise<any> => {
    const { data } = await apiClient.put(
      `/workflow/${workflowId}/draft/`,
      feGraphPayloadToOrch(payload),
    )
    return data
  },

  /** Publish workflow draft to live graph (orch: PUT /workflow/<id>/graph/). */
  save: async (workflowId: string, payload: { nodes: any[]; connectors: any[] }): Promise<any> => {
    const { data } = await apiClient.put(
      `/workflow/${workflowId}/graph/`,
      feGraphPayloadToOrch(payload),
      // A refused save names its locked nodes; the editor renders that itself.
      OWNS_COMPONENT_REFUSAL,
    )
    return orchGraphToFeGraph(data)
  },

  /** Discard the in-Redis draft (orch: DELETE /workflow/<id>/draft/). */
  discard: async (workflowId: string): Promise<any> => {
    const { data } = await apiClient.delete(`/workflow/${workflowId}/draft/`)
    return orchGraphToFeGraph(data)
  },

  /**
   * Component catalog. The orch returns a flat list at `GET /workflow/components/`
   * where each item carries `group: { id, name, display_name, icon, order } | null`.
   * `DrawerDags` renders an accordion of `{ group, group_fa, nodes }[]`, so we
   * group here: components with a group land under `group.display_name || group.name`;
   * groupless components land under a synthetic "Other" bucket. Groups are ordered
   * by `group.order` then name, with "Other" pinned last.
   */
  getNodeTypes: async (): Promise<any> => {
    const { data } = await apiClient.get('/workflow/components/')
    const items: any[] = Array.isArray(data) ? data : data?.data ?? []

    const OTHER = 'Other'
    type Bucket = { group: string; group_fa: string | null; order: number; nodes: any[] }
    const buckets = new Map<string, Bucket>()

    for (const c of items) {
      const g = c?.group ?? null
      const key = g ? (g.display_name || g.name) : OTHER
      if (!buckets.has(key)) {
        buckets.set(key, {
          group: key,
          group_fa: null,
          order: g ? (typeof g.order === 'number' ? g.order : 0) : Number.MAX_SAFE_INTEGER,
          nodes: [],
        })
      }
      buckets.get(key)!.nodes.push(orchComponentToNodeDetails(c))
    }

    return Array.from(buckets.values())
      .sort((a, b) => a.order - b.order || a.group.localeCompare(b.group))
      .map(({ group, group_fa, nodes }) => ({ group, group_fa, nodes }))
  },

  /**
   * Single component + its frontend-shaped schema. Two orch calls in parallel:
   *   GET /workflow/components/{id}/
   *   GET /workflow/components/{id}/frontend-schema/
   * Merged so ConfigModal sees `config_schema = {id, map, routes}` as before.
   */
  getNodeTypeById: async (componentId: string): Promise<any> => {
    const [meta, frontendSchema] = await Promise.all([
      apiClient.get(`/workflow/components/${componentId}/`).then((r) => r.data),
      apiClient.get(`/workflow/components/${componentId}/frontend-schema/`).then((r) => r.data),
    ])
    return {
      ...orchComponentToNodeDetails(meta),
      config_schema: frontendSchema,
    }
  },

  /**
   * Validate a config object against a component's schema.
   * Payload changed to `{ component_id, config }`.
   * Response adapter: orch returns `{valid, errors:[{path, message}]}`;
   * FE's ConfigModal expects Django-style `{field_name: [error_string]}`.
   */
  validateNodeConfig: async (payload: { component_id: string; config: Record<string, any> }): Promise<Record<string, string[]>> => {
    const { data } = await apiClient.post(
      `/workflow/components/${payload.component_id}/validate-config/`,
      { config: payload.config },
    )
    if (data?.valid) return {}
    const out: Record<string, string[]> = {}
    for (const e of (data?.errors ?? [])) {
      if (!e?.path) continue
      // Orch emits JSON-pointer paths (`/host`, `/options/timeout`); formik
      // expects dotted field names (`host`, `options.timeout`). Strip the
      // leading slash and convert internal slashes.
      const field = String(e.path).replace(/^\//, '').replace(/\//g, '.')
        ; (out[field] ||= []).push(e.message ?? 'Invalid value')
    }
    return out
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  // Used by AiGeneratorModal and useAiAssistant hook.
  getAiAssistStatus: async (workflowId: string): Promise<any> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/ai-assist/`)
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  sendWorkflowAiMessage: async (workflowId: string, message: string): Promise<any> => {
    const { data } = await apiClient.post(`/workflow/${workflowId}/ai-assist/`, { message })
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  getWorkflowAiStatus: async (workflowId: string): Promise<any> => {
    const { data } = await apiClient.get(`/workflow/${workflowId}/ai-status/`)
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  testNode: async (payload: any): Promise<any> => {
    const { data } = await apiClient.post('/workflow/test-node/', payload)
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  getNodeAiStatus: async (nodeTypeId: string): Promise<any> => {
    const { data } = await apiClient.get(`/workflow/node-types/${nodeTypeId}/ai-status/`)
    return data
  },

  // LEGACY: kept from pre-orchestrator version, not present in reference project.
  sendNodeAiMessage: async (nodeTypeId: string, message: string): Promise<any> => {
    const { data } = await apiClient.post(`/workflow/node-types/${nodeTypeId}/ai-assist/`, { message })
    return data
  },
}
